// Explore's model: the dimension catalogue (what each facet is, how it's labelled, what
// tapping a bucket does), the filter assembly that turns the shared store state into
// /api/facets requests, the fetch hook every panel uses (abortable, memoized, era-merged),
// and the client-side lenses (lift, anniversaries) that compose two facet calls.
//
// One primitive, many views: every panel on the page is `useFacet(dim)` over the SAME
// filters Browse lists with — Explore is "what's in this slice", Browse is "show me it".

import { computed, batch } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  fetchComposerEntity,
  fetchFacets,
  fetchPieceEntity,
  type ComposerEntity,
  type FacetBucket,
  type FacetEra,
  type FacetFilters,
  type FacetResult,
  type PieceEntity,
} from "../data/api";
import {
  archiveEnabled,
  archiveSources,
  certParam,
  cities,
  composers,
  disabledSources,
  excludedArchiveKeys,
  exploreAlive,
  exploreEra,
  explorePremiere,
  exploreScope,
  exploreWindow,
  historicalCities,
  liveEnabled,
  LIVE_SOURCE_KEY,
  meta,
  orgs,
  radiusCenter,
  radiusKm,
  selectedCityId,
  selectedComposerId,
  selectedCountry,
  selectedGenre,
  selectedOrgId,
  selectedPiece,
  selectedPieceKey,
  setCityFilter,
  setPieceFilter,
  setTimeWindow,
  GENRE_OPTIONS,
  todayISO,
} from "./store";
import { countryDisplayName } from "../utils/format";
import type { Piece } from "../types";

// ── Dimension catalogue ───────────────────────────────────────────────

export type DimId =
  | "composer" | "piece" | "org" | "org_type" | "city" | "country" | "genre"
  | "year" | "month" | "dow" | "hour" | "era" | "alive" | "quality"
  | "decade" | "source" | "status" | "premiere";

export interface DimDef {
  id: DimId;
  title: string;
  /** Which era tables carry the dimension. */
  eras: FacetEra[];
  /** Keys are shared across eras (composer ids, piece keys…) → both eras can be summed. */
  mergeable: boolean;
  /** Bounded cardinality: the API returns it whole; the UI never pages it. */
  small: boolean;
  /** Natural (chronological/ordinal) order instead of by count. */
  keyOrder?: boolean;
  /** Tapping a bucket narrows the slice. */
  drillable: boolean;
  /** Lift lens available (the API accepts keys= for it). */
  liftable?: boolean;
}

const BOTH: FacetEra[] = ["live", "archive"];

export const DIM_DEFS: Record<DimId, DimDef> = {
  composer: { id: "composer", title: "Composers", eras: BOTH, mergeable: true, small: false, drillable: true, liftable: true },
  piece: { id: "piece", title: "Pieces", eras: BOTH, mergeable: true, small: false, drillable: true, liftable: true },
  org: { id: "org", title: "Organizations", eras: ["live"], mergeable: false, small: false, drillable: true, liftable: true },
  org_type: { id: "org_type", title: "Organization type", eras: ["live"], mergeable: false, small: true, drillable: false },
  city: { id: "city", title: "Cities", eras: BOTH, mergeable: true, small: false, drillable: true, liftable: true },
  country: { id: "country", title: "Countries", eras: BOTH, mergeable: true, small: true, drillable: true, liftable: true },
  genre: { id: "genre", title: "Genre", eras: ["live"], mergeable: false, small: true, drillable: true },
  year: { id: "year", title: "Years", eras: BOTH, mergeable: true, small: true, keyOrder: true, drillable: true },
  month: { id: "month", title: "Months", eras: BOTH, mergeable: true, small: true, keyOrder: true, drillable: true },
  dow: { id: "dow", title: "Day of week", eras: ["live"], mergeable: false, small: true, keyOrder: true, drillable: false },
  hour: { id: "hour", title: "Start time", eras: ["live"], mergeable: false, small: true, keyOrder: true, drillable: false },
  era: { id: "era", title: "Composer era", eras: BOTH, mergeable: true, small: true, keyOrder: true, drillable: true },
  alive: { id: "alive", title: "Living composers", eras: BOTH, mergeable: true, small: true, drillable: true },
  quality: { id: "quality", title: "Listing quality", eras: ["live"], mergeable: false, small: true, keyOrder: true, drillable: false },
  decade: { id: "decade", title: "Decades", eras: ["archive"], mergeable: false, small: true, keyOrder: true, drillable: true },
  source: { id: "source", title: "Archive sources", eras: ["archive"], mergeable: false, small: true, drillable: true },
  status: { id: "status", title: "Certainty", eras: ["archive"], mergeable: false, small: true, drillable: false },
  premiere: { id: "premiere", title: "Premieres", eras: ["archive"], mergeable: false, small: true, drillable: true },
};

// ── Which eras the current slice spans ────────────────────────────────

/** The archive layer joins in only when the slice can reach it: past/all scope, archive
 *  sources enabled, and no live-only filter (org, genre) — the same applicability rule
 *  data/queries.ts uses for the unified list. */
export const exploreEras = computed<FacetEra[]>(() => {
  const eras: FacetEra[] = [];
  if (liveEnabled.value) eras.push("live");
  const until = meta.value?.historical?.until ?? "2025-01-01";
  const w = exploreWindow.value;
  const archiveOk =
    archiveEnabled.value &&
    exploreScope.value !== "upcoming" &&
    selectedOrgId.value === null &&
    !selectedGenre.value &&
    (!w.from || w.from < until);
  if (archiveOk) eras.push("archive");
  return eras;
});

// ── Filter assembly ───────────────────────────────────────────────────

/** The /api/facets filters for one era, read from the shared store — the browse
 *  vocabulary plus Explore's scope window and its three extra narrowings. */
export function facetFiltersFor(era: FacetEra): FacetFilters {
  const w = exploreWindow.value;
  const center = radiusCenter.value;
  const km = radiusKm.value;
  const useRadius = center !== null && km !== null;
  const common: FacetFilters = {
    composer: selectedComposerId.value,
    piece: selectedPieceKey.value,
    city: useRadius ? null : selectedCityId.value,
    near: useRadius ? [center.lat, center.lon] : null,
    km: useRadius ? km : null,
    country: selectedCountry.value || null,
    from: w.from,
    to: w.to,
    xera: exploreEra.value,
    alive: exploreAlive.value,
  };
  if (era === "live") {
    return { ...common, org: selectedOrgId.value, genre: selectedGenre.value || null };
  }
  return {
    ...common,
    xsrc: excludedArchiveKeys.value,
    cert: certParam.value,
    premiere: explorePremiere.value,
  };
}

/** The same filters with the ENTITY narrowings removed — what the lift lens compares
 *  against ("…than concerts in general, in this window"). */
function globalFiltersFor(era: FacetEra): FacetFilters {
  const f = facetFiltersFor(era);
  return {
    ...f,
    composer: null, piece: null, city: null, near: null, km: null, country: null,
    org: null, genre: null, xera: null, alive: false, premiere: false,
  };
}

/** True when some filter scopes the slice to an entity/era — the state in which
 *  "distinctive" (lift) and "often programmed with" make sense. */
export const sliceIsScoped = computed(
  () =>
    selectedComposerId.value !== null || selectedPieceKey.value !== null ||
    selectedOrgId.value !== null || selectedCityId.value !== null ||
    !!selectedCountry.value || !!selectedGenre.value || exploreEra.value !== null ||
    exploreAlive.value || explorePremiere.value,
);

// ── Fetching ──────────────────────────────────────────────────────────

export interface Bucket {
  /** String form of the key — the identity used for merging, exclusion, labels. */
  key: string;
  k: string | number | null;
  n: number;
  /** Concerts on/after today (only meaningful for the live era). */
  up: number;
  label?: string | null;
  composer?: string | null;
  composer_id?: number | null;
}

export interface FacetState {
  buckets: Bucket[];
  /** Concerts in the slice (both eras summed) — what shares are of. */
  matched: number;
  matchedUp: number;
  loading: boolean;
  error: string | null;
  truncated: boolean;
  /** Eras that actually answered (a live-only dim under a both-era slice says so). */
  eras: FacetEra[];
}

const EMPTY: FacetState = { buckets: [], matched: 0, matchedUp: 0, loading: true, error: null, truncated: false, eras: [] };

interface FetchDescriptor {
  dim: DimId;
  eras: FacetEra[];
  filters: Partial<Record<FacetEra, FacetFilters>>;
  limit: number;
  offset: number;
  by?: string | null;
  /** Restrict to these bucket keys (≤50): the lift lens and the sheet's catalogue search
   *  ask "how many in THIS slice for exactly these", bypassing the ranked head. */
  keys?: string[] | null;
}

/** Memoized per descriptor: reopening a panel or stepping Back is instant, and two
 *  panels asking the same question (the page summary + the Places panel) share one
 *  request. Bounded so a long session can't grow it without limit. */
const cache = new Map<string, Promise<FacetState>>();
const CACHE_MAX = 300;

function keyOf(b: FacetBucket): string {
  return b.k === null || b.k === undefined ? "unknown" : String(b.k);
}

async function runDescriptor(d: FetchDescriptor, signal?: AbortSignal): Promise<FacetState> {
  const def = DIM_DEFS[d.dim];
  const eras = d.eras.filter((e) => def.eras.includes(e));
  // A both-era slice on a single-era dim: answer from the era that has it, and say so.
  const use = eras.length ? (def.mergeable ? eras : [eras[0]]) : [];
  if (!use.length) return { ...EMPTY, loading: false, eras: [] };
  const today = todayISO();
  if (use.length > 1 && !def.small && !d.by && !d.keys?.length) return runMerged(d, use, today, signal);
  const results: FacetResult[] = await Promise.all(
    use.map((era) =>
      fetchFacets(era, d.dim, d.filters[era]!, {
        limit: d.limit,
        offset: d.offset,
        anchor: era === "live" ? today : null,
        by: d.by ?? null,
        keys: d.keys ?? null,
        signal,
      }),
    ),
  );
  const merged = new Map<string, Bucket>();
  let matched = 0;
  let matchedUp = 0;
  let truncated = false;
  for (const r of results) {
    matched += r.matched;
    matchedUp += r.matchedUp ?? 0;
    truncated = truncated || r.truncated;
    for (const b of r.buckets) {
      const key = keyOf(b);
      const prev = merged.get(key);
      if (prev) {
        prev.n += b.n;
        prev.up += b.up ?? 0;
        if (!prev.label && b.label) { prev.label = b.label; prev.composer = b.composer; prev.composer_id = b.composer_id; }
      } else {
        merged.set(key, { key, k: b.k, n: b.n, up: b.up ?? 0, label: b.label, composer: b.composer, composer_id: b.composer_id });
      }
    }
  }
  const buckets = [...merged.values()];
  if (def.keyOrder) buckets.sort((a, b) => compareKeys(a.key, b.key));
  else buckets.sort((a, b) => b.n - a.n || compareKeys(a.key, b.key));
  return { buckets, matched, matchedUp, loading: false, error: null, truncated, eras: use };
}

/** Rows the API returns per call, and the deepest row it will page to (MAX_LIMIT + MAX_OFFSET). */
const API_PAGE = 200;
const API_DEPTH = 1200;
/** keys= accepts at most 50 per call; cap the exact-count fill so a deep page can't fan out. */
const FILL_BATCH = 50;
const FILL_MAX = 400;

/** A both-era ranking. Each era is ranked separately by the API, so summing two pages
 *  (the old path) ranked a composer by whichever half it happened to be in: page 2 of the
 *  "All" sheet opened with 409 after page 1 ended on 208, and 56 composers were on both
 *  pages with split counts (measured 2026-09-30). Instead: read a deeper head of each era,
 *  and for every key that could make the top `offset + limit` but is missing from one
 *  era's head, ask that era for its exact count (keys=). Rows are ranked from 0 every
 *  time, so callers page by asking for a longer head, not by appending. */
async function runMerged(d: FetchDescriptor, use: FacetEra[], today: string, signal?: AbortSignal): Promise<FacetState> {
  const want = d.offset + d.limit;
  const depth = Math.min(API_DEPTH, Math.max(want * 2, 100));
  const call = (era: FacetEra, opts: { limit: number; offset?: number; keys?: string[] }) =>
    fetchFacets(era, d.dim, d.filters[era]!, { ...opts, anchor: era === "live" ? today : null, signal });

  const heads = await Promise.all(
    use.map(async (era) => {
      const offsets: number[] = [];
      for (let o = 0; o < depth; o += API_PAGE) offsets.push(o);
      const pages = await Promise.all(offsets.map((o) => call(era, { limit: Math.min(API_PAGE, depth - o), offset: o })));
      const rows = pages.flatMap((r) => r.buckets);
      const last = pages[pages.length - 1];
      // An era whose head is complete contributes exactly 0 for any key it doesn't list.
      const complete = !last.truncated && last.buckets.length < Math.min(API_PAGE, depth - offsets[offsets.length - 1]);
      const floor = complete || !rows.length ? 0 : Math.min(...rows.map((b) => b.n));
      return { era, rows, matched: pages[0].matched, matchedUp: pages[0].matchedUp ?? 0, truncated: pages.some((p) => p.truncated), floor };
    }),
  );

  const merged = new Map<string, Bucket & { seen: Set<FacetEra> }>();
  for (const h of heads) {
    for (const b of h.rows) {
      const key = keyOf(b);
      const prev = merged.get(key);
      if (prev) {
        if (prev.seen.has(h.era)) continue; // a row repeated across two API pages
        prev.seen.add(h.era);
        prev.n += b.n;
        prev.up += b.up ?? 0;
        if (!prev.label && b.label) { prev.label = b.label; prev.composer = b.composer; prev.composer_id = b.composer_id; }
      } else {
        merged.set(key, { key, k: b.k, n: b.n, up: b.up ?? 0, label: b.label, composer: b.composer, composer_id: b.composer_id, seen: new Set([h.era]) });
      }
    }
  }

  // Which partial keys could still reach the top `want`? Upper bound = seen + each missing era's floor.
  const all = [...merged.values()];
  const upper = (b: (typeof all)[number]) => b.n + heads.reduce((s, h) => s + (b.seen.has(h.era) ? 0 : h.floor), 0);
  const byLower = [...all].sort((a, b) => b.n - a.n);
  const cutoff = byLower.length >= want ? byLower[want - 1].n : 0;
  const partial = all
    .filter((b) => b.key !== "unknown" && upper(b) > b.n && upper(b) >= cutoff)
    .sort((a, b) => upper(b) - upper(a))
    .slice(0, FILL_MAX);
  await Promise.all(
    heads.map(async (h) => {
      if (!h.floor) return;
      const missing = partial.filter((b) => !b.seen.has(h.era)).map((b) => b.key);
      const batches: string[][] = [];
      for (let i = 0; i < missing.length; i += FILL_BATCH) batches.push(missing.slice(i, i + FILL_BATCH));
      const results = await Promise.all(batches.map((keys) => call(h.era, { limit: FILL_BATCH, keys })));
      for (const r of results) {
        for (const b of r.buckets) {
          const m = merged.get(keyOf(b));
          if (!m || m.seen.has(h.era)) continue;
          m.seen.add(h.era);
          m.n += b.n;
          m.up += b.up ?? 0;
        }
      }
    }),
  );

  const ranked = [...merged.values()]
    .sort((a, b) => b.n - a.n || compareKeys(a.key, b.key))
    .map(({ seen: _seen, ...b }) => b);
  return {
    buckets: ranked.slice(d.offset, want),
    matched: heads.reduce((s, h) => s + h.matched, 0),
    matchedUp: heads.reduce((s, h) => s + h.matchedUp, 0),
    loading: false,
    error: null,
    truncated: ranked.length > want || heads.some((h) => h.truncated),
    eras: use,
  };
}

function compareKeys(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  if (a === "unknown") return 1;
  if (b === "unknown") return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function loadFacet(d: FetchDescriptor, signal?: AbortSignal): Promise<FacetState> {
  const key = JSON.stringify(d);
  const hit = cache.get(key);
  if (hit) return hit;
  const p = runDescriptor(d, signal).catch((err) => {
    cache.delete(key); // don't memoize failures/aborts
    throw err;
  });
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value as string);
  cache.set(key, p);
  return p;
}

/** Build the descriptor for a dimension from the current store state. Reading the
 *  signals here (inside a component render) is what subscribes the panel to them. */
export function describe(
  dim: DimId,
  opts: { limit?: number; offset?: number; by?: string | null; own?: "only" | "others" | null } = {},
): FetchDescriptor {
  const eras = exploreEras.value;
  const filters: Partial<Record<FacetEra, FacetFilters>> = {};
  for (const era of eras) filters[era] = opts.own ? { ...facetFiltersFor(era), own: opts.own } : facetFiltersFor(era);
  return { dim, eras, filters, limit: opts.limit ?? 50, offset: opts.offset ?? 0, by: opts.by ?? null };
}

/** The panel hook: re-fetches whenever the descriptor changes, aborts the stale request,
 *  and keeps the previous buckets on screen (dimmed via `loading`) while the next arrive
 *  — no blank flashes on a phone while the reader taps through drill-downs. */
export function useFacet(dim: DimId, opts: { limit?: number; enabled?: boolean; own?: "only" | "others" | null } = {}): FacetState {
  const enabled = opts.enabled ?? true;
  const descriptor = describe(dim, { limit: opts.limit, own: opts.own });
  const key = enabled ? JSON.stringify(descriptor) : "";
  const [state, setState] = useState<FacetState>(EMPTY);
  // Stale rows stay on screen (dimmed) only while the SAME dimension re-fetches; a panel
  // re-used for another dimension starts blank, or composer ids would render as pieces.
  const lastDim = useRef(dim);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const sameDim = lastDim.current === dim;
    lastDim.current = dim;
    setState((prev) => (sameDim ? { ...prev, loading: true, error: null } : EMPTY));
    loadFacet(descriptor, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setState(res);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, loading: false, error: err instanceof Error ? err.message : String(err) }));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return enabled ? state : { ...EMPTY, loading: false };
}

// ── Lift lens ─────────────────────────────────────────────────────────

export interface Lift {
  /** local share ÷ global share; >1 = over-represented in this slice. */
  lift: number;
  globalN: number;
}

/** For the given buckets (a scoped slice), fetch their GLOBAL counts in the same window
 *  and compute lift. One extra request per era with keys=. */
export function useLift(dim: DimId, state: FacetState, enabled: boolean): Map<string, Lift> {
  const [lifts, setLifts] = useState<Map<string, Lift>>(new Map());
  const def = DIM_DEFS[dim];
  const eras = exploreEras.value.filter((e) => def.eras.includes(e));
  const keys = state.buckets.slice(0, 40).map((b) => b.key).filter((k) => k !== "unknown");
  const globals: Partial<Record<FacetEra, FacetFilters>> = {};
  for (const era of eras) globals[era] = globalFiltersFor(era);
  const key = enabled && def.liftable && keys.length && state.matched > 0
    ? JSON.stringify({ dim, eras, globals, keys })
    : "";

  useEffect(() => {
    if (!key) { setLifts(new Map()); return; }
    const controller = new AbortController();
    const today = todayISO();
    Promise.all(
      eras.map((era) =>
        fetchFacets(era, dim, globals[era]!, { keys, limit: 50, anchor: era === "live" ? today : null, signal: controller.signal }),
      ),
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const globalN = new Map<string, number>();
        let globalMatched = 0;
        for (const r of results) {
          globalMatched += r.matched;
          for (const b of r.buckets) globalN.set(keyOf(b), (globalN.get(keyOf(b)) ?? 0) + b.n);
        }
        const out = new Map<string, Lift>();
        if (globalMatched > 0) {
          for (const b of state.buckets) {
            const g = globalN.get(b.key);
            if (!g) continue;
            const localShare = b.n / state.matched;
            const globalShare = g / globalMatched;
            out.set(b.key, { lift: globalShare > 0 ? localShare / globalShare : 0, globalN: g });
          }
        }
        setLifts(out);
      })
      .catch(() => {
        if (!controller.signal.aborted) setLifts(new Map());
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return lifts;
}

// ── Labels ────────────────────────────────────────────────────────────

const composerById = computed(() => new Map(composers.value.map((c) => [c.id, c])));
const orgById = computed(() => new Map(orgs.value.map((o) => [o.id, o])));
const cityById = computed(() => {
  const m = new Map(cities.value.map((c) => [c.id, c]));
  for (const c of historicalCities.value) if (!m.has(c.id)) m.set(c.id, c);
  return m;
});
const sourceByKey = computed(() => new Map(archiveSources.value.map((s) => [s.key, s])));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOWS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function eraLabel(k: string): string {
  if (k === "unknown") return "Birth year unknown";
  const y = Number(k);
  return `Born ${y}–${y + 49}`;
}

/** Human label for a bucket. Ids resolve against the static facet files; unknown ids
 *  (a composer exported after the facet file was cached) fall back to "#id". */
export function bucketLabel(dim: DimId, b: Bucket): string {
  const k = b.key;
  switch (dim) {
    case "composer": return composerById.value.get(Number(k))?.name ?? `Composer #${k}`;
    case "org": return orgById.value.get(Number(k))?.name ?? `Organization #${k}`;
    case "piece": return b.label ?? k;
    case "city": {
      const c = cityById.value.get(Number(k));
      if (!c) return `City #${k}`;
      // "Berlin, Germany" under a Germany filter says Germany twice.
      return c.country_code === selectedCountry.value ? c.name : `${c.name}, ${countryDisplayName(c.country_code, c.country_name)}`;
    }
    case "country": return k === "unknown" ? "Unknown country" : countryDisplayName(k);
    case "genre": return k === "unclassified" ? "Unclassified" : GENRE_OPTIONS.find((g) => g.value === k)?.label ?? k;
    case "org_type": return k === "unknown" ? "Unknown type" : k.replace(/_/g, " ");
    case "year": return k;
    case "month": {
      const [y, m] = k.split("-");
      return `${MONTHS[Number(m) - 1] ?? m} ${y}`;
    }
    case "dow": return DOWS[Number(k)] ?? k;
    case "hour": return k === "unknown" ? "No stated time" : `${k.padStart(2, "0")}:00`;
    case "era": return eraLabel(k);
    case "alive": return k === "living" ? "Living at the time" : k === "dead" ? "No longer living" : "Unknown";
    case "quality": return k === "80" ? "80–99" : `${k}–${Number(k) + 19}`;
    case "decade": return `${k}s`;
    case "source": return sourceByKey.value.get(k)?.title ?? (k === "unknown" ? "Unknown source" : k);
    case "status": return k.charAt(0).toUpperCase() + k.slice(1);
    case "premiere": return k === "premiere" ? "Premieres" : "Other performances";
  }
}

/** Secondary line for a bucket (the composer under a piece, the type after an org). */
export function bucketDetail(dim: DimId, b: Bucket): string | null {
  if (dim === "piece") return b.composer ?? null;
  // Organization type ("opera company") was tried as a row badge: it truncated the name on
  // phones and said little. The Organization type lens covers it.
  return null;
}

// ── Drill-down ────────────────────────────────────────────────────────

/** Tapping a bucket narrows the slice. Returns false when the bucket isn't a filter
 *  (an "unknown" bucket, or a view-only dimension). */
export function drill(dim: DimId, b: Bucket): boolean {
  const k = b.key;
  if (k === "unknown" || k === "unclassified") return false;
  switch (dim) {
    case "composer":
      batch(() => { selectedComposerId.value = Number(k); });
      return true;
    case "org":
      selectedOrgId.value = Number(k);
      return true;
    case "piece": {
      const piece: Piece = {
        key: k,
        label: b.label ?? k,
        composer: b.composer ?? null,
        composer_id: b.composer_id ?? null,
        part_kind: null,
        count: b.n,
      };
      setPieceFilter(piece);
      return true;
    }
    case "city":
      setCityFilter(Number(k), "manual");
      return true;
    case "country":
      batch(() => {
        selectedCountry.value = k;
        // A city outside the newly picked country would contradict it.
        const city = selectedCityId.value !== null ? cityById.value.get(selectedCityId.value) : null;
        if (city && city.country_code !== k) setCityFilter(null);
      });
      return true;
    case "genre":
      selectedGenre.value = k;
      return true;
    case "era":
      exploreEra.value = Number(k);
      return true;
    case "alive":
      if (k !== "living") return false;
      exploreAlive.value = true;
      return true;
    case "premiere":
      if (k !== "premiere") return false;
      explorePremiere.value = true;
      return true;
    case "year":
      setTimeWindow(`${k}-01-01`, `${k}-12-31`);
      return true;
    case "decade":
      setTimeWindow(`${k}-01-01`, `${Number(k) + 9}-12-31`);
      return true;
    case "month": {
      const [y, m] = k.split("-").map(Number);
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      setTimeWindow(`${k}-01`, `${k}-${String(last).padStart(2, "0")}`);
      return true;
    }
    case "source": {
      // Solo this archive source; keep the live layer as it is.
      const all = new Set<string>(archiveSources.value.map((s) => s.key));
      all.delete(k);
      if (!liveEnabled.value) all.add(LIVE_SOURCE_KEY);
      disabledSources.value = all;
      return true;
    }
    default:
      return false;
  }
}

// ── Anniversaries ─────────────────────────────────────────────────────

export interface Anniversary {
  composerId: number;
  name: string;
  count: number;
  year: number;
  years: number;
  kind: "birth" | "death";
  /** The birth or death year the anniversary counts from. */
  since: number;
}

/** Composers with a round (multiple of 50, ≥ 50) birth or death anniversary in the given
 *  years, ranked by how much they're performed. Pure client-side over composers.json. */
export function anniversaries(years: number[], limit = 12): Anniversary[] {
  const out: Anniversary[] = [];
  for (const c of composers.value) {
    for (const year of years) {
      if (c.birth_year && (year - c.birth_year) % 50 === 0 && year - c.birth_year >= 100) {
        out.push({ composerId: c.id, name: c.name, count: c.count, year, years: year - c.birth_year, kind: "birth", since: c.birth_year });
      }
      if (c.death_year && (year - c.death_year) % 50 === 0 && year - c.death_year >= 50) {
        out.push({ composerId: c.id, name: c.name, count: c.count, year, years: year - c.death_year, kind: "death", since: c.death_year });
      }
    }
  }
  out.sort((a, b) => b.count - a.count || a.year - b.year);
  return out.slice(0, limit);
}

// ── Focus (entity profiles) ───────────────────────────────────────
//
// Explore has two faces. With no entity selected it is a dashboard over the whole
// slice — the landing. With one it is that entity's PROFILE: a header of facts and
// headline numbers, then only the panels that say something about it (an organization
// has cities and composers; a piece has who plays it, when and where — never a
// "Pieces" list of the other works that happened to share its evenings). The most
// specific selection wins: a piece implies its composer, an organization sits in a city.

export type FocusKind = "piece" | "composer" | "org" | "city" | "country";

export interface Focus {
  kind: FocusKind;
  /** Piece key, or the numeric id / country code as a string. */
  key: string;
}

export const exploreFocus = computed<Focus | null>(() => {
  if (selectedPieceKey.value) return { kind: "piece", key: selectedPieceKey.value };
  if (selectedComposerId.value !== null) return { kind: "composer", key: String(selectedComposerId.value) };
  if (selectedOrgId.value !== null) return { kind: "org", key: String(selectedOrgId.value) };
  if (selectedCityId.value !== null) return { kind: "city", key: String(selectedCityId.value) };
  if (selectedCountry.value) return { kind: "country", key: selectedCountry.value };
  return null;
});

/** Under a piece focus the composer filter is implied by the piece (the pick from a
 *  composer's list sets both, so removing the piece steps back to the composer). A chip
 *  for it would be one whose removal changes nothing on screen — hidden. */
export const composerImpliedByPiece = computed(
  () =>
    selectedPieceKey.value !== null &&
    selectedComposerId.value !== null &&
    selectedPiece.value?.key === selectedPieceKey.value &&
    selectedPiece.value?.composer_id === selectedComposerId.value,
);

interface EntityState<T> {
  data: T | null;
  loading: boolean;
}

/** Memoized per key — a profile revisited (Back, a chip removed and re-added) is instant. */
const entityCache = new Map<string, Promise<unknown>>();

function useEntity<T>(key: string | null, load: (signal: AbortSignal) => Promise<T | null>): EntityState<T> {
  const [state, setState] = useState<EntityState<T>>({ data: null, loading: key !== null });
  useEffect(() => {
    if (key === null) {
      setState({ data: null, loading: false });
      return;
    }
    const controller = new AbortController();
    setState({ data: null, loading: true });
    let p = entityCache.get(key) as Promise<T | null> | undefined;
    if (!p) {
      p = load(controller.signal).catch((err) => {
        entityCache.delete(key);
        throw err;
      });
      if (entityCache.size >= 200) entityCache.delete(entityCache.keys().next().value as string);
      entityCache.set(key, p);
    }
    p.then((data) => {
      if (!controller.signal.aborted) setState({ data, loading: false });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ data: null, loading: false });
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

export function usePieceEntity(key: string | null): EntityState<PieceEntity> {
  return useEntity(key === null ? null : `piece:${key}`, (signal) => fetchPieceEntity(key!, { signal }));
}

export function useComposerEntity(id: number | null): EntityState<ComposerEntity> {
  return useEntity(id === null ? null : `composer:${id}`, (signal) => fetchComposerEntity(id!, { signal }));
}
